import React from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

export interface ChatMessage {
  id: string;
  message: string;
  timestamp: Date;
  type: 'user' | 'system' | 'assistant';
}

export interface ModificationPanelProps {
  chatHistory: ChatMessage[];
  chatMessage: string;
  setChatMessage: (value: string) => void;
  isChatProcessing: boolean;
  onChatSubmit: () => void;
  onChatCancel: () => void;
}

const ModificationPanel: React.FC<ModificationPanelProps> = ({
  chatHistory,
  chatMessage,
  setChatMessage,
  isChatProcessing,
  onChatSubmit,
  onChatCancel,
}) => (
<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
  {/* Chat History */}
  <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
    {chatHistory.length === 0 ? null : (
      chatHistory.map(msg => (
        <Box 
          key={msg.id} 
          sx={{ 
            mb: 1, 
            p: 1, 
            bgcolor: msg.type === 'user' ? '#e3f2fd' : msg.type === 'system' ? '#fff3e0' : '#f5f5f5',
            borderRadius: 1,
            textAlign: msg.type === 'user' ? 'right' : 'left'
          }}
        >
          <Typography variant="body2">{msg.message}</Typography>
          <Typography variant="caption" color="text.secondary">
            {msg.timestamp.toLocaleTimeString()}
          </Typography>
        </Box>
      ))
    )}
  </Box>
  
  {/* Quick Action Dropdown */}
  <Box sx={{ mb: 2 }}>
    <FormControl fullWidth size="small">
      <InputLabel>Select a modification prompt...</InputLabel>
      <Select
        value=""
        label="Select a modification prompt..."
        onChange={(e) => setChatMessage(e.target.value)}
        disabled={isChatProcessing}
      >
        <MenuItem value="Make the passage 1-2 paragraphs shorter while maintaining the main ideas and narrative flow">
          Shorten Passage - Reduce length by 1-2 paragraphs while keeping key content
        </MenuItem>
        <MenuItem value="Increase the difficulty of the questions by incorporating more complex vocabulary, higher-order thinking skills, and multi-step reasoning">
          More Challenging - Elevate question difficulty with complex vocabulary and critical thinking
        </MenuItem>
        <MenuItem value="Add 3-4 additional vocabulary-focused questions that test word meaning in context, word relationships, and nuanced definitions">
          More Vocabulary - Add questions testing context clues, word relationships, and precise meanings
        </MenuItem>
        <MenuItem value="Simplify the language and sentence structure to make the passage more accessible while keeping the same content and message">
          Simplify Language - Make the passage more accessible with simpler vocabulary and sentence structure
        </MenuItem>
        <MenuItem value="Add 2-3 additional questions covering different aspects of the text, including literal comprehension, inference, and text structure">
          Add Questions - Include 2-3 more questions covering various comprehension levels
        </MenuItem>
        <MenuItem value="Revise the questions to focus more on inference, analysis, synthesis, and evaluation rather than literal recall">
          Higher-Order Thinking - Focus on inference, analysis, and evaluation over recall
        </MenuItem>
        <MenuItem value="Change the passage to historical fiction genre while maintaining appropriate reading level and question alignment">
          Change to Historical Fiction - Transform the passage into a historical fiction narrative
        </MenuItem>
        <MenuItem value="Increase the passage length by 2-3 paragraphs with additional relevant details, examples, or narrative development">
          Lengthen Passage - Add 2-3 paragraphs with supporting details and development
        </MenuItem>
        <MenuItem value="Incorporate specific Bloom's Taxonomy levels into the questions with clear labeling of Remember, Understand, Apply, Analyze, Evaluate, and Create">
          Add Bloom's Taxonomy - Label questions with specific cognitive levels (Remember, Analyze, etc.)
        </MenuItem>
      </Select>
    </FormControl>
  </Box>
  
  {/* Input Box */}
  <Box sx={{ display: 'flex', gap: 2 }}>
    <TextField
      fullWidth
      multiline
      maxRows={4}
      value={chatMessage}
      onChange={(e) => setChatMessage(e.target.value)}
      placeholder="Ask to modify the passage or questions (e.g., 'Make the passage shorter', 'Add more challenging questions')"
      disabled={isChatProcessing}
      onKeyPress={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onChatSubmit();
        }
      }}
    />
    <Button
      variant="contained"
      onClick={onChatSubmit}
      disabled={!chatMessage.trim() || isChatProcessing}
      startIcon={<SendIcon />}
      sx={{ minWidth: 100 }}
    >
      {isChatProcessing ? 'Processing...' : 'Send'}
    </Button>
    {isChatProcessing && (
      <Button
        variant="outlined"
        color="error"
        onClick={onChatCancel}
      >
        Cancel
      </Button>
    )}
  </Box>
  
  {/* Helpful Examples */}
  <Box sx={{ mt: 2 }}>
    <Typography variant="caption" color="text.secondary">
      💡 Or try custom requests like: "Make the passage 2 paragraphs longer" • "Change genre to historical fiction" • "Add questions targeting specific TEKS standards" • "Increase vocabulary complexity for advanced learners"
    </Typography>
  </Box>
</Paper>
);

export default ModificationPanel;
